import { useCallback, useEffect, useId, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso } from '../componenti/Pezzi.tsx'
import { Bottone, Campo, Modale, Riquadro, Scelta } from '../componenti/base.tsx'
import { IconaCarica, IconaPiu } from '../componenti/Icone.tsx'
import { caricaLogo, indirizzoLogo, problemaFileLogo, type LogoBreve } from '../servizi/stampa.ts'

/**
 * Loghi delle stampe (462): quelli di un progetto, nella pagina del progetto, e il logo CIDIS, nella
 * pagina dei progetti per la Direzione. I file si caricano come i documenti e l'API li controlla alla
 * conferma; qui si fa il primo controllo (tipo e peso) per non far aspettare un rifiuto.
 */

const messaggio = (e: unknown) => (e instanceof ErroreApi ? e.message
  : e instanceof Error ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const AIUTO_FILE = 'PNG o JPEG, al massimo 2 MB. Va meglio un PNG con lo sfondo trasparente o bianco, alto almeno '
  + '200 pixel; niente SVG.'

type LoghiDelProgetto = { loghi: (LogoBreve & { id: string })[]; cidis: LogoBreve; massimo: number; gestisce: boolean }

/** Scelta del file con il controllo immediato di tipo e peso. */
function SceltaFile({ file, cambia, errore }: { file: File | null; cambia: (f: File | null) => void; errore: string | null }) {
  const id = useId()
  return (
    <div className={`campo${errore ? ' campo-errato' : ''}`}>
      <label htmlFor={id}>File del logo<span className="campo-obbligatorio" aria-hidden="true"> *</span></label>
      <input id={id} type="file" accept="image/png,image/jpeg" aria-describedby={`${id}-nota`}
             onChange={(e) => cambia(e.target.files?.[0] ?? null)} />
      {errore
        ? <p className="campo-errore" id={`${id}-nota`} role="alert">{errore}</p>
        : <p className="campo-aiuto" id={`${id}-nota`}>{file ? `${file.name} · ${Math.ceil(file.size / 1024)} kB` : AIUTO_FILE}</p>}
    </div>
  )
}

/** Modulo di caricamento: nome e file. `percorso` e' la rotta di prenotazione. */
function ModuloCaricamento({ percorso, nomeIniziale = '', fatto, annulla }: {
  percorso: string; nomeIniziale?: string; fatto: () => void; annulla: () => void
}) {
  const [nome, setNome] = useState(nomeIniziale)
  const [file, setFile] = useState<File | null>(null)
  const [erroreFile, setErroreFile] = useState<string | null>(null)
  const [erroreNome, setErroreNome] = useState<string | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  async function invia() {
    const problemaNome = nome.trim() ? null : 'Dai un nome al logo, per esempio il nome del finanziatore.'
    const problemaFile = file ? problemaFileLogo(file) : 'Scegli il file del logo.'
    setErroreNome(problemaNome); setErroreFile(problemaFile); setErrore(null)
    if (problemaNome || problemaFile || !file) return
    setInCorso(true)
    try {
      await caricaLogo(percorso, nome.trim(), file)
      fatto()
    } catch (e) {
      setErrore(messaggio(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); void invia() }}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Campo etichetta="Nome del logo" valore={nome} cambia={setNome} maxLength={120} obbligatorio errore={erroreNome}
             aiuto="Si legge solo qui e nell’elenco dei loghi, per esempio «Ministero dell’Interno – FAMI»." />
      <SceltaFile file={file} cambia={(f) => { setFile(f); setErroreFile(f ? problemaFileLogo(f) : null) }} errore={erroreFile} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" icona={<IconaCarica />} inCorso={inCorso}>Carica il logo</Bottone>
      </div>
    </form>
  )
}

/** Riquadro della pagina del progetto: i loghi che la stampa della scheda propone per questo progetto. */
export function LoghiProgetto({ progettoId }: { progettoId: string }) {
  const [dati, setDati] = useState<LoghiDelProgetto | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [finestra, setFinestra] = useState<'carica' | 'riusa' | null>(null)
  const [disponibili, setDisponibili] = useState<(LogoBreve & { id: string; progetti: number })[]>([])
  const [daRiusare, setDaRiusare] = useState('')
  const [inCorso, setInCorso] = useState(false)

  const carica = useCallback(() => {
    api.get<LoghiDelProgetto>(`/progetti/${progettoId}/loghi`)
      .then((d) => { setDati(d); setErrore(null) })
      .catch((e) => setErrore(messaggio(e)))
  }, [progettoId])
  useEffect(carica, [carica])

  async function apriRiuso() {
    setErrore(null)
    try {
      const r = await api.get<{ disponibili: (LogoBreve & { id: string; progetti: number })[] }>('/loghi')
      const presenti = new Set(dati?.loghi.map((l) => l.id))
      setDisponibili(r.disponibili.filter((l) => !presenti.has(l.id)))
      setDaRiusare('')
      setFinestra('riusa')
    } catch (e) { setErrore(messaggio(e)) }
  }

  async function azione(chiamata: () => Promise<unknown>) {
    setErrore(null); setInCorso(true)
    try { await chiamata(); carica() } catch (e) { setErrore(messaggio(e)) } finally { setInCorso(false) }
  }

  const sposta = (indice: number, verso: -1 | 1) => {
    if (!dati) return
    const ordine = dati.loghi.map((l) => l.id)
    const [logo] = ordine.splice(indice, 1)
    ordine.splice(indice + verso, 0, logo)
    void azione(() => api.put(`/progetti/${progettoId}/loghi`, { ordine }))
  }

  if (!dati) return errore ? <Avviso tipo="errore">{errore}</Avviso> : null
  const pieno = dati.loghi.length >= dati.massimo

  return (
    <Riquadro titolo="Loghi nelle stampe" azioni={dati.gestisce && !pieno ? (
      <>
        <Bottone variante="secondario" dimensione="piccolo" onClick={() => void apriRiuso()}>Usa un logo già caricato</Bottone>
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />} onClick={() => setFinestra('carica')}>
          Aggiungi un logo
        </Bottone>
      </>
    ) : undefined}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <p className="persona-nota-sezione">
        Quando si stampa la scheda utente di una persona con un percorso in questo progetto, questi loghi compaiono
        nell’intestazione del foglio dopo il logo CIDIS, nell’ordine indicato. Chi stampa può toglierne qualcuno.
        {pieno && dati.gestisce ? ` Il progetto ha già ${dati.massimo} loghi, il massimo: per aggiungerne uno, togline un altro.` : ''}
      </p>
      <ol className="loghi-elenco">
        <li className="loghi-voce loghi-voce-cidis">
          <img src={indirizzoLogo(dati.cidis)} alt="" />
          <span>{dati.cidis.nome} <span className="tenue">(logo CIDIS, su tutte le stampe)</span></span>
        </li>
        {dati.loghi.map((l, i) => (
          <li className="loghi-voce" key={l.id}>
            <img src={indirizzoLogo(l)} alt="" />
            <span>{l.nome} <span className="tenue piccolo">{l.larghezza} × {l.altezza} pixel</span></span>
            {dati.gestisce && (
              <span className="loghi-comandi">
                <Bottone variante="discreto" dimensione="piccolo" disabled={inCorso || i === 0}
                         title={`Sposta prima «${l.nome}»`} onClick={() => sposta(i, -1)}>Su</Bottone>
                <Bottone variante="discreto" dimensione="piccolo" disabled={inCorso || i === dati.loghi.length - 1}
                         title={`Sposta dopo «${l.nome}»`} onClick={() => sposta(i, 1)}>Giù</Bottone>
                <Bottone variante="discreto" dimensione="piccolo" disabled={inCorso}
                         title={`Togli «${l.nome}» dalle stampe del progetto`}
                         onClick={() => void azione(() => api.elimina(`/progetti/${progettoId}/loghi/${l.id}`))}>Togli</Bottone>
              </span>
            )}
          </li>
        ))}
      </ol>
      {!dati.loghi.length && (
        <p className="tenue piccolo">
          Il progetto non ha loghi propri{dati.gestisce ? ': aggiungi quelli del finanziatore, del capofila o del programma.' : '.'}
        </p>
      )}

      {finestra === 'carica' && (
        <Modale titolo="Aggiungi un logo al progetto" chiudi={() => setFinestra(null)}>
          <ModuloCaricamento percorso={`/progetti/${progettoId}/loghi`} annulla={() => setFinestra(null)}
                             fatto={() => { setFinestra(null); carica() }} />
        </Modale>
      )}
      {finestra === 'riusa' && (
        <Modale titolo="Usa un logo già caricato" chiudi={() => setFinestra(null)} azioni={(
          <>
            <Bottone variante="discreto" onClick={() => setFinestra(null)}>Annulla</Bottone>
            <Bottone variante="primario" disabled={!daRiusare} inCorso={inCorso}
                     onClick={() => void azione(async () => {
                       await api.post(`/progetti/${progettoId}/loghi/${daRiusare}`)
                       setFinestra(null)
                     })}>Aggiungi</Bottone>
          </>
        )}>
          {disponibili.length
            ? (
              <>
                <Scelta etichetta="Logo" valore={daRiusare} cambia={setDaRiusare} vuota="— scegli un logo —"
                        opzioni={disponibili.map((l) => ({
                          valore: l.id,
                          etichetta: `${l.nome}${l.progetti ? ` (in ${l.progetti} ${l.progetti === 1 ? 'progetto' : 'progetti'})` : ''}`,
                        }))}
                        aiuto="Lo stesso logo può servire a più progetti: il file resta uno." />
                {daRiusare && (
                  <img className="loghi-anteprima" src={indirizzoLogo({ id: daRiusare })} alt="Anteprima del logo scelto" />
                )}
              </>
              )
            : <p>Non ci sono altri loghi già caricati: usa «Aggiungi un logo».</p>}
        </Modale>
      )}
    </Riquadro>
  )
}

/** Riquadro della Direzione: il logo CIDIS di tutte le stampe, sostituibile e ripristinabile. */
export function LogoCidis() {
  const [cidis, setCidis] = useState<LogoBreve | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [finestra, setFinestra] = useState<'carica' | 'ripristina' | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const carica = useCallback(() => {
    api.get<{ cidis: LogoBreve }>('/loghi')
      .then((r) => { setCidis(r.cidis); setErrore(null) })
      .catch((e) => setErrore(messaggio(e)))
  }, [])
  useEffect(carica, [carica])

  async function ripristina() {
    setInCorso(true); setErrore(null)
    try { await api.elimina('/loghi/cidis'); setFinestra(null); carica() } catch (e) { setErrore(messaggio(e)) } finally { setInCorso(false) }
  }

  if (!cidis) return errore ? <Avviso tipo="errore">{errore}</Avviso> : <Attesa />
  return (
    <Riquadro titolo="Logo CIDIS nelle stampe" azioni={(
      <>
        {cidis.id && <Bottone variante="discreto" dimensione="piccolo" onClick={() => setFinestra('ripristina')}>Torna al logo originale</Bottone>}
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaCarica />} onClick={() => setFinestra('carica')}>Sostituisci</Bottone>
      </>
    )}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <div className="loghi-voce loghi-voce-cidis">
        <img src={indirizzoLogo(cidis)} alt="" />
        <span>{cidis.id ? `${cidis.nome}, caricato in sostituzione` : 'Logo originale dell’applicazione'}</span>
      </div>
      <p className="persona-nota-sezione">
        Compare per primo nell’intestazione di ogni scheda utente stampata e di ogni PDF. Chi stampa può toglierlo da
        un singolo foglio.
      </p>
      {finestra === 'carica' && (
        <Modale titolo="Sostituisci il logo CIDIS" chiudi={() => setFinestra(null)}>
          <ModuloCaricamento percorso="/loghi/cidis" nomeIniziale="CIDIS" annulla={() => setFinestra(null)}
                             fatto={() => { setFinestra(null); carica() }} />
        </Modale>
      )}
      {finestra === 'ripristina' && (
        <Modale titolo="Torna al logo originale" chiudi={() => setFinestra(null)} azioni={(
          <>
            <Bottone variante="discreto" onClick={() => setFinestra(null)}>Indietro</Bottone>
            <Bottone variante="primario" inCorso={inCorso} onClick={() => void ripristina()}>Torna all’originale</Bottone>
          </>
        )}>
          <p>Le stampe useranno di nuovo il logo originale dell’applicazione. Il logo caricato resta in archivio, disattivato.</p>
        </Modale>
      )}
    </Riquadro>
  )
}
